import { CorpOrganizationStatus } from '../enums';
import { z } from 'zod';

import { CivilDateDto } from './civilDate';
import { RebrandThemeDto } from './rebrand';

export const CreateCorpOrganizationDto = z
  .object({
    name: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit'),
    contactPerson: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit'),
    contactEmail: z
      .string()
      .trim()
      .email('VALIDATION__InvalidEmail')
      .nullable()
      .optional(),
    contractStartDate: CivilDateDto.nullable().optional(),
    contractEndDate: CivilDateDto.nullable().optional(),
  })
  .strict();

export type ApiCreateCorpOrganizationDto = z.infer<
  typeof CreateCorpOrganizationDto
>;

export const UpdateCorpOrganizationDto = z
  .object({
    name: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    contactPerson: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    contactEmail: z
      .string()
      .trim()
      .email('VALIDATION__InvalidEmail')
      .nullable()
      .optional(),
    contractStartDate: CivilDateDto.nullable().optional(),
    contractEndDate: CivilDateDto.nullable().optional(),
    logoName: z.string().max(255).nullable().optional(),
  })
  .strict();

export type ApiUpdateCorpOrganizationDto = z.infer<
  typeof UpdateCorpOrganizationDto
>;

export const QueryCorpOrganizationsDto = z
  .object({
    search: z.string().trim().max(255).optional(),
    offset: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    orderBy: z.string().min(1).max(255).optional(),
    orderDirection: z.string().min(1).max(4).optional(),
    status: z
      .string()
      .toUpperCase()
      .pipe(z.enum(['ACTIVE', 'FROZEN', 'ALL']))
      .optional()
      .or(z.literal('')),
  })
  .strict();

export type ApiQueryCorpOrganizationsDto = z.infer<
  typeof QueryCorpOrganizationsDto
>;

export const CorpOrganizationDto = z.object({
  id: z.string(),
  name: z.string(),
  contactPerson: z.string(),
  contactEmail: z.string().nullable(),
  status: z.nativeEnum(CorpOrganizationStatus),
  contractStartDate: z.string().nullable(),
  contractEndDate: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  codesTotal: z.number().optional(),
  codesUsed: z.number().optional(),
  theme: z.record(z.string()).nullable().optional(),
  logoName: z.string().nullable().optional(),
  logoUrl: z.string().nullable().optional(),
});

export type ApiCorpOrganizationDto = z.infer<typeof CorpOrganizationDto>;

// Corp rebrand (D-2/D-12): the corp write reuses the org's editor payload —
// the two brand colours (strict 6-hex) plus the `glow`, `surface` and
// `calendarColours` settings. The validated object FULLY REPLACES
// `CorpOrganizations.theme`.
export const UpdateCorpOrganizationRebrandThemeDto = RebrandThemeDto;

export type ApiUpdateCorpOrganizationRebrandThemeDto = z.infer<
  typeof UpdateCorpOrganizationRebrandThemeDto
>;
